// src/pages/ProfileGate/steps/ProfileSummaryStep.jsx

import React from "react";
import { useNavigate } from "react-router-dom";

import {
  OnboardingFormCard,
  OnboardingInterstitial,
} from "../OnboardingVisuals";

import {
  useProfileGateDraft,
} from "../../../hooks/useProfileGateDraft";

const SUMMARY_PENGUIN_URL =
  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/Onboarding/Gwin_Arms_Crossed.png";

function SummaryBlock({
  eyebrow,
  title,
  description,
}) {
  return (
    <OnboardingFormCard>
      <div>
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#FF914D]">
          {eyebrow}
        </p>

        <h2 className="mt-2 text-[17px] font-bold leading-snug text-[#222222] sm:text-[18px]">
          {title}
        </h2>

        {description ? (
          <p className="mt-2 text-[14px] leading-relaxed text-[#757575] sm:text-[15px]">
            {description}
          </p>
        ) : null}
      </div>
    </OnboardingFormCard>
  );
}

function getFocusDisplay(value) {
  switch (value) {
    case "Take better control of my business money":
      return "Better control of your business money";

    case "Build better financial habits":
      return "Build better financial habits";

    case "Understand my business numbers better":
      return "Understand your business numbers better";

    case "Get more organised for future funding":
      return "Get more organised for future funding";

    case "I’m not sure yet":
      return "Explore what deserves your attention first";

    default:
      return value || "Your business finances";
  }
}

function getTrackingSummary(value) {
  switch (value) {
    case "Accountant or bookkeeper":
      return "You’re working with an accountant or bookkeeper.";

    case "Accounting software":
      return "You’re already using accounting software to keep track of your money.";

    case "Spreadsheet":
      return "You’re already keeping records in a spreadsheet.";

    case "Notebook or manual records":
      return "You’re currently keeping track of your money manually.";

    case "Bank account/statements":
      return "You’re mainly using your bank account or statements to keep track of your money.";

    case "I don’t have a regular system yet":
      return "You don’t have a regular money-tracking system yet.";

    default:
      return value
        ? `You currently use ${value.toLowerCase()} to keep track of your money.`
        : "We’ll help you build a clearer way to manage your business money.";
  }
}

export default function ProfileSummaryStep() {
  const navigate =
    useNavigate();

  const {
    loading,
    draft,
  } = useProfileGateDraft();

  if (loading) {
    return null;
  }

  const selectedFocus =
    Array.isArray(
      draft.business_priorities
    )
      ? draft.business_priorities[0] ||
        ""
      : "";

  const financeTrackingMethod =
    draft.finance_tracking_method ||
    "";

  const focusDisplay =
    getFocusDisplay(
      selectedFocus
    );

  const trackingSummary =
    getTrackingSummary(
      financeTrackingMethod
    );

  return (
    <OnboardingInterstitial
      message="Thanks. Here’s where we’ll start:"
      showBack={true}
      showSpeechPenguin={false}
      nextLabel="Continue"
      onBack={() =>
        navigate(
          "/profile-gate/financial-setup"
        )
      }
      onNext={() =>
        navigate(
          "/profile-gate/learning-plan"
        )
      }
    >
      <div className="mx-auto mt-6 w-full max-w-[560px]">
        <div className="mx-auto mb-6 w-[42%] max-w-[190px]">
          <img
            src={
              SUMMARY_PENGUIN_URL
            }
            alt="Gwin"
            className="h-auto w-full object-contain"
            loading="eager"
            decoding="async"
            draggable="false"
          />
        </div>

        <div className="space-y-4">
          <SummaryBlock
            eyebrow="Your main focus"
            title={
              focusDisplay
            }
          />

          <SummaryBlock
            eyebrow="How you manage things today"
            title={
              trackingSummary
            }
          />

          <SummaryBlock
            eyebrow="Your first Learn step"
            title="Start with Financial Control Focus"
            description="Build a clear starting point for what deserves your attention first."
          />

          <SummaryBlock
            eyebrow="Your first Action step"
            title="Set up your financial action calendar"
            description="Answer a few simple questions so we can help you stay on track with financial actions."
          />
        </div>
      </div>
    </OnboardingInterstitial>
  );
}